/**
 * JobProgress — Modal reutilizable que muestra el progreso de un job en background.
 *
 * Uso:
 *   const [jobId, setJobId] = useState(null);
 *
 *   const handleSubmit = async () => {
 *     const res = await api.post('/api/notifications/send', {...});
 *     setJobId(res.data.job_id);
 *   };
 *
 *   {jobId && (
 *     <JobProgress
 *       jobId={jobId}
 *       title="Enviando notificaciones"
 *       onClose={() => setJobId(null)}
 *       onDone={(job) => console.log('Terminado:', job)}
 *     />
 *   )}
 */
import { useState, useEffect, useRef } from 'react';
import api from '../services/api';

export default function JobProgress({ jobId, title = 'Procesando', onClose, onDone }) {
  const [job, setJob]       = useState(null);
  const [error, setError]   = useState(null);
  const pollRef             = useRef(null);
  const doneCalledRef       = useRef(false);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/api/jobs/${jobId}`);
        if (cancelled) return;
        const j = res.data.data;
        setJob(j);

        // Si terminó (done o failed) → notificar y dejar de hacer polling
        if (j.status === 'done' || j.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          if (!doneCalledRef.current) {
            doneCalledRef.current = true;
            if (onDone) onDone(j);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Error consultando estado del job');
      }
    };

    poll();                                  // primer fetch inmediato
    pollRef.current = setInterval(poll, 2000); // luego cada 2 segundos

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, onDone]);

  if (!jobId) return null;

  // ─── Estilos inline ─────────────────────────────────────────────
  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  };
  const modal = {
    background: '#fff', borderRadius: 12, padding: 32,
    minWidth: 420, maxWidth: '90vw',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  };
  const titleStyle = { fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 8 };
  const subStyle   = { color: '#666', fontSize: 13, marginBottom: 20 };
  const barBg      = { background: '#f3f4f6', borderRadius: 8, height: 12, overflow: 'hidden', marginBottom: 12 };
  const statsRow   = { display: 'flex', gap: 16, marginBottom: 16, fontSize: 13 };
  const statBox    = { flex: 1, padding: 10, background: '#f9fafb', borderRadius: 6, textAlign: 'center' };
  const btn        = { padding: '10px 20px', borderRadius: 6, border: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 };

  // Datos derivados
  const processed   = job?.processed   || 0;
  const total       = job?.total       || 0;
  const okCount     = job?.ok_count    || 0;
  const errorCount  = job?.error_count || 0;
  const percent     = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isRunning   = job?.status === 'running' || job?.status === 'queued';
  const isDone      = job?.status === 'done';
  const isFailed    = job?.status === 'failed';

  const summary = job?.result_summary;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={titleStyle}>{title}</h3>
        <p style={subStyle}>
          {isRunning && '⏳ Procesando en background... puedes cerrar esta ventana, el trabajo continúa.'}
          {isDone    && '✅ Trabajo completado correctamente.'}
          {isFailed  && '❌ El trabajo falló.'}
          {error     && `Error: ${error}`}
        </p>

        {/* Barra de progreso */}
        <div style={barBg}>
          <div style={{
            width: `${percent}%`,
            background: isFailed ? '#dc1414' : (isDone ? '#16a34a' : '#dc1414'),
            height: '100%',
            transition: 'width 0.3s ease',
          }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#666' }}>
            {processed} / {total || '?'}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{percent}%</span>
        </div>

        {/* Stats */}
        <div style={statsRow}>
          <div style={statBox}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{okCount}</div>
            <div style={{ fontSize: 11, color: '#666' }}>Exitosos</div>
          </div>
          <div style={statBox}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#dc1414' }}>{errorCount}</div>
            <div style={{ fontSize: 11, color: '#666' }}>Errores</div>
          </div>
        </div>

        {/* Mensaje de error si failed */}
        {isFailed && job?.error_message && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 12, color: '#991b1b' }}>
            {job.error_message}
          </div>
        )}

        {/* Resumen final si done */}
        {isDone && summary && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 12, color: '#166534' }}>
            <strong>Resumen final:</strong>
            <pre style={{ margin: '4px 0 0', fontSize: 11 }}>{JSON.stringify(summary, null, 2)}</pre>
          </div>
        )}

        {/* Botón cerrar */}
        <div style={{ textAlign: 'right' }}>
          {isRunning ? (
            <button style={{ ...btn, background: '#f3f4f6', color: '#374151' }} onClick={onClose}>
              Cerrar (seguirá en background)
            </button>
          ) : (
            <button style={{ ...btn, background: '#dc1414', color: '#fff' }} onClick={onClose}>
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
